import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Handshake, MessageCircle } from 'lucide-react';
import PremiumCard from '../ui/PremiumCard.jsx';
import ScrollReveal from './ScrollReveal.jsx';
import { assetUrl } from '../../utils/assetUrl.js';
import publicCta from '../../content/publicCta.json';
import {
  CONVENIO_CATEGORIES,
  getConvenioCategoryLabel,
  pickOneConvenioPerCategory,
} from '../../constants/convenioCategories.js';
import { getConvenioWhatsAppHref } from '../../utils/convenioWhatsApp.js';

const HOME_SKELETON_COUNT = CONVENIO_CATEGORIES.length;

function ConvenioHomeCard({ c, delayMs }) {
  const waHref = getConvenioWhatsAppHref(c);
  return (
    <ScrollReveal delayMs={delayMs} className="min-w-0">
      <PremiumCard hover className="flex h-full flex-col overflow-hidden !p-0">
        <div className="relative flex aspect-[16/10] items-center justify-center bg-white p-4 ring-1 ring-slate-200">
          {c.imagen ? (
            <img
              src={assetUrl(c.imagen)}
              alt=""
              className="max-h-full max-w-full object-contain"
            />
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-2 text-slate-400">
              <Handshake className="h-10 w-10 text-accent-lime" aria-hidden />
              <span className="text-xs font-medium uppercase tracking-wider">Convenio</span>
            </div>
          )}
          {c.categoria ? (
            <span className="absolute left-3 top-3 rounded-full bg-brand-navy px-3.5 py-1.5 text-sm font-semibold uppercase tracking-wide text-white ring-1 ring-[#1acb12]/45">
              {getConvenioCategoryLabel(c.categoria)}
            </span>
          ) : null}
        </div>
        <div className="flex flex-1 flex-col p-6">
          <h3 className="font-display text-xl font-semibold text-white">{c.nombre}</h3>
          <p className="mt-3 line-clamp-3 flex-1 text-justify text-sm leading-relaxed text-slate-400">
            {c.descripcion || 'Consulta condiciones y vigencia con la cooperativa.'}
          </p>
          {waHref ? (
            <a
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#20bd5a]"
            >
              <MessageCircle className="h-4 w-4" aria-hidden />
              {publicCta.conveniosWhatsAppLabel}
            </a>
          ) : null}
        </div>
      </PremiumCard>
    </ScrollReveal>
  );
}

export default function ConveniosHomeSection({ items, loading, embedded = false }) {
  const list = useMemo(() => pickOneConvenioPerCategory(items), [items]);

  const ctaClass = embedded
    ? 'inline-flex items-center gap-2 rounded-full border border-[#1acb12]/40 bg-black/20 px-5 py-2.5 text-sm font-semibold text-accent-lime backdrop-blur-sm transition hover:border-[#1acb12]/70 hover:bg-black/30 hover:text-white'
    : 'inline-flex items-center gap-2 rounded-full border border-[#1acb12]/40 bg-white px-5 py-2.5 text-sm font-semibold text-accent-lime shadow-sm transition hover:border-[#1acb12]/70 hover:text-[#0d2b7f]';

  const content = (
    <>
      <ScrollReveal className="mx-auto max-w-3xl text-center">
        <p className="text-xs font-semibold uppercase tracking-[0.28em] text-accent-lime">Beneficios</p>
        <h2
          className={
            embedded
              ? 'mt-3 font-display text-3xl font-bold text-white md:text-4xl'
              : 'mt-4 font-display text-3xl font-bold text-accent-blue md:text-4xl'
          }
        >
          Convenios para nuestros asociados
        </h2>
        <p className={embedded ? 'mt-4 text-slate-400' : 'mt-5 text-lg text-slate-600'}>
          Descuentos y aliados en salud, recreación y seguridad. Consulta los convenios destacados.
        </p>
      </ScrollReveal>

      <div className={`grid gap-8 md:grid-cols-2 lg:grid-cols-3 ${embedded ? 'mt-10' : 'mt-12'}`}>
        {loading &&
          Array.from({ length: HOME_SKELETON_COUNT }).map((_, i) => (
            <div
              key={i}
              className="h-96 animate-skeleton-shimmer rounded-2xl bg-white/10 ring-1 ring-white/10"
            />
          ))}

        {!loading && list.map((c, i) => <ConvenioHomeCard key={c.id} c={c} delayMs={Math.min(i, 2) * 55} />)}
      </div>

      {!loading && list.length > 0 && (
        <ScrollReveal className="mt-10 text-center">
          <Link to="/convenios" className={ctaClass}>
            {publicCta.conveniosSeeAllLabel}
            <ChevronRight className="h-4 w-4" aria-hidden />
          </Link>
        </ScrollReveal>
      )}

      {!loading && list.length === 0 && (
        <p className={`mt-10 text-center ${embedded ? 'text-slate-400' : 'text-slate-600'}`}>
          No hay convenios publicados por ahora.
        </p>
      )}
    </>
  );

  if (embedded) {
    return <div id="convenios">{content}</div>;
  }

  return (
    <section
      id="convenios"
      className="scroll-mt-24 border-t border-slate-200 bg-slate-50 py-16 lg:py-24"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-8">{content}</div>
    </section>
  );
}
